import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import ApproveConfirmModal from './ApproveConfirmModal';
import RejectMemberModal from './RejectMemberModal';
import { useAdmin } from '../../contexts/AdminContext';
import { formatRelativeTime } from '../../utils/dateUtils';
import { getGravatarUrl } from '../../utils/gravatar';
import {
  statistics,
  monthlyRevenue,
  membershipTypeDistribution,
  recentActivity as stubRecentActivity,
} from '../../utils/stubData';

function Dashboard() {
  const { pendingMembers, approveMember, rejectMember } = useAdmin();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const handleApprove = async () => {
    const success = await approveMember(selectedMember.id);
    if (success) {
      setApproveModalOpen(false);
      setSelectedMember(null);
      // Could show success toast here
    }
  };

  const handleReject = async reason => {
    const success = await rejectMember(selectedMember.id, reason);
    if (success) {
      setRejectModalOpen(false);
      setSelectedMember(null);
      // Could show success toast here
    }
  };

  const openApproveModal = (e, member) => {
    e.preventDefault();
    setSelectedMember(member);
    setApproveModalOpen(true);
  };

  const openRejectModal = (e, member) => {
    e.preventDefault();
    setSelectedMember(member);
    setRejectModalOpen(true);
  };

  const totalPages = Math.ceil(pendingMembers.length / itemsPerPage);
  const paginatedMembers = pendingMembers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const revenueChange =
    ((statistics.revenueThisMonth - statistics.revenueLastMonth) / statistics.revenueLastMonth) *
    100;
  const membersChange =
    ((statistics.newMembersThisMonth - statistics.newMembersLastMonth) /
      statistics.newMembersLastMonth) *
    100;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-hdlcc-textDark uppercase">Admin Dashboard</h2>
          <p className="text-hdlcc-textMuted">Welcome back, Admin</p>
        </div>
      </div>

      {/* Key Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Link to="/admin/members" className="block">
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card hover:shadow-lg border border-gray-200 hover:border-hdlcc-accentRed transition-all duration-200">
            <h4 className="text-hdlcc-textMuted text-sm">Total Members</h4>
            <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">{statistics.totalMembers}</p>
            <div className="mt-2 text-sm">
              <span className="text-hdlcc-accentGreen">{statistics.activeMembers} active</span>
              {' • '}
              <span className="text-hdlcc-accentRed">{statistics.expiredMembers} expired</span>
            </div>
          </div>
        </Link>

        <Link to="/admin/requests" className="block">
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card hover:shadow-lg border border-gray-200 hover:border-hdlcc-accentRed transition-all duration-200">
            <h4 className="text-hdlcc-textMuted text-sm">Pending Approvals</h4>
            <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">
              {statistics.pendingApprovals}
            </p>
            <div className="mt-2 text-sm text-yellow-600">Requires attention</div>
          </div>
        </Link>

        <Link to="/admin/payments" className="block">
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card hover:shadow-lg border border-gray-200 hover:border-hdlcc-accentRed transition-all duration-200">
            <h4 className="text-hdlcc-textMuted text-sm">Revenue (This Month)</h4>
            <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">
              £{statistics.revenueThisMonth.toLocaleString()}
            </p>
            <div className="mt-2 text-sm">
              <span
                className={revenueChange >= 0 ? 'text-hdlcc-accentGreen' : 'text-hdlcc-accentRed'}
              >
                {revenueChange >= 0 ? '↑' : '↓'} {Math.abs(revenueChange).toFixed(1)}%
              </span>
              {' vs last month'}
            </div>
          </div>
        </Link>

        <Link to="/admin/members?dateRange=1month" className="block">
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card hover:shadow-lg border border-gray-200 hover:border-hdlcc-accentRed transition-all duration-200">
            <h4 className="text-hdlcc-textMuted text-sm">Expiring Soon</h4>
            <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">
              {statistics.expiringWithin30Days}
            </p>
            <div className="mt-2 text-sm text-yellow-600">Within 30 days</div>
          </div>
        </Link>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Revenue & Memberships Chart */}
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">
            Monthly Revenue & Memberships
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart
              data={monthlyRevenue.map(item => ({
                ...item,
                scaledMembers: item.members * 70, // Scale members to be visible on same axis as revenue
              }))}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fill: '#95a5a6', fontSize: 12 }} stroke="#95a5a6" />
              <YAxis tick={{ fill: '#95a5a6', fontSize: 12 }} stroke="#95a5a6" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                }}
                formatter={(value, name) => {
                  if (name === 'revenue') return [`£${value.toLocaleString()}`, 'Revenue'];
                  if (name === 'scaledMembers') return [Math.round(value / 70), 'New Members'];
                  return [value, name];
                }}
              />
              <Legend
                wrapperStyle={{ paddingTop: '10px' }}
                formatter={value => {
                  if (value === 'revenue') return 'Revenue (£)';
                  if (value === 'scaledMembers') return 'New Memberships';
                  return value;
                }}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#e74c3c"
                strokeWidth={2}
                dot={{ fill: '#e74c3c', r: 4 }}
                activeDot={{ r: 6 }}
                name="revenue"
              />
              <Line
                type="monotone"
                dataKey="scaledMembers"
                stroke="#2ecc71"
                strokeWidth={2}
                dot={{ fill: '#2ecc71', r: 4 }}
                activeDot={{ r: 6 }}
                name="scaledMembers"
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-2 text-sm text-hdlcc-textMuted text-center">
            Total Year: £{statistics.revenueThisYear.toLocaleString()}
          </div>
        </div>

        {/* Membership Type Distribution */}
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Membership Distribution</h3>
          <div className="space-y-3">
            {membershipTypeDistribution.map((item, index) => (
              <div key={item.type}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-hdlcc-textDark">{item.type}</span>
                  <span className="text-hdlcc-textMuted">
                    {item.count} ({item.percentage.toFixed(1)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      index === 0
                        ? 'bg-blue-500'
                        : index === 1
                          ? 'bg-green-500'
                          : index === 2
                            ? 'bg-yellow-500'
                            : index === 3
                              ? 'bg-purple-500'
                              : 'bg-pink-500'
                    }`}
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">New Members</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-textDark">
            {statistics.newMembersThisMonth}
          </p>
          <div className="mt-2 text-sm">
            <span
              className={membersChange >= 0 ? 'text-hdlcc-accentGreen' : 'text-hdlcc-accentRed'}
            >
              {membersChange >= 0 ? '↑' : '↓'} {Math.abs(membersChange).toFixed(1)}%
            </span>
            {' this month'}
          </div>
        </div>

        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Renewals This Month</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-textDark">
            {statistics.renewalsThisMonth}
          </p>
          <div className="mt-2 text-sm text-hdlcc-textMuted">Payment processing</div>
        </div>

        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Upcoming Renewals</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-textDark">
            {statistics.expiringWithin90Days}
          </p>
          <div className="mt-2 text-sm text-hdlcc-textMuted">Within 90 days</div>
        </div>
      </div>
      <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card mb-6 border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-hdlcc-textDark">Pending Approvals</h3>
          <Link
            to="/admin/members?status=pending"
            className="text-hdlcc-accentRed hover:text-red-600 text-sm"
          >
            View All
          </Link>
        </div>
        {paginatedMembers.map(member => (
          <Link
            key={member.id}
            to={`/admin/members/${member.id}`}
            className="block mb-2 hover:bg-gray-50 rounded transition-colors duration-200"
          >
            <div className="flex justify-between items-center p-2">
              <div className="flex items-center space-x-2">
                <img
                  src={getGravatarUrl(member.email, 32)}
                  alt={`${member.firstName} ${member.lastName}`}
                  className="w-8 h-8 rounded-full border border-hdlcc-accentRed"
                />
                <div>
                  <strong className="text-hdlcc-textDark">
                    {member.firstName} {member.lastName}
                  </strong>
                  <p className="text-hdlcc-textMuted text-sm">
                    {member.membershipType} Membership • Submitted{' '}
                    {formatRelativeTime(member.submittedAt)}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  className="bg-hdlcc-accentGreen text-white px-2 py-1 rounded hover:bg-green-600 transition-colors"
                  onClick={e => openApproveModal(e, member)}
                >
                  Approve
                </button>
                <button
                  className="bg-hdlcc-bgWhite text-hdlcc-textDark px-2 py-1 rounded hover:bg-gray-100 border border-gray-300 transition-colors"
                  onClick={e => openRejectModal(e, member)}
                >
                  Reject
                </button>
              </div>
            </div>
          </Link>
        ))}
        {totalPages > 1 && (
          <div className="flex justify-center space-x-2 mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-2 py-1 rounded ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-hdlcc-accentRed hover:bg-red-600 text-white'
              }`}
            >
              Previous
            </button>
            <span className="text-hdlcc-textDark px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`px-2 py-1 rounded ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-hdlcc-accentRed hover:bg-red-600 text-white'
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
      <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-hdlcc-textDark">Recent Activity</h3>
          <Link to="/admin/audit-log" className="text-hdlcc-accentRed hover:text-red-600 text-sm">
            View All
          </Link>
        </div>
        <div className="space-y-3">
          {stubRecentActivity.map(activity => (
            <div
              key={activity.id}
              className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded"
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0 ${
                  activity.type === 'payment'
                    ? 'bg-green-600'
                    : activity.type === 'new_member'
                      ? 'bg-blue-600'
                      : activity.type === 'renewal'
                        ? 'bg-purple-600'
                        : activity.type === 'email_sent'
                          ? 'bg-yellow-600'
                          : 'bg-gray-600'
                }`}
              >
                {activity.type === 'payment'
                  ? '£'
                  : activity.type === 'new_member'
                    ? '+'
                    : activity.type === 'renewal'
                      ? '↻'
                      : activity.type === 'email_sent'
                        ? '✉'
                        : 'i'}
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-hdlcc-textDark">{activity.memberName}</p>
                    <p className="text-sm text-hdlcc-textMuted">{activity.description}</p>
                  </div>
                  {activity.amount && (
                    <span className="text-hdlcc-accentGreen font-semibold">
                      £{activity.amount.toFixed(2)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formatRelativeTime(activity.timestamp)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <RejectMemberModal
        isOpen={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedMember(null);
        }}
        onReject={handleReject}
        memberName={selectedMember ? `${selectedMember.firstName} ${selectedMember.lastName}` : ''}
      />
      <ApproveConfirmModal
        isOpen={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          setSelectedMember(null);
        }}
        onConfirm={handleApprove}
        memberName={selectedMember ? `${selectedMember.firstName} ${selectedMember.lastName}` : ''}
      />
    </div>
  );
}

export default Dashboard;
